import { Button } from '@reelplicate/ui';
import { Alert, AlertDescription, AlertTitle } from '@reelplicate/ui/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import type { FallbackProps } from 'react-error-boundary';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';

import { getErrorMessage } from '@/lib/errors';

/** Fallback for react-error-boundary: shows the error and offers a retry. */
export function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <Alert variant="destructive" role="alert">
      <AlertTriangle />
      <AlertTitle>Something went wrong</AlertTitle>
      <AlertDescription>
        <p>{getErrorMessage(error)}</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={resetErrorBoundary}>
          Try again
        </Button>
      </AlertDescription>
    </Alert>
  );
}

/** react-router `errorElement`: renders for thrown route errors and unknown paths. */
export function RouteErrorFallback({ notFound = false }: { notFound?: boolean }) {
  const error = useRouteError();
  const is404 = notFound || (isRouteErrorResponse(error) && error.status === 404);

  return (
    <div className="mx-auto max-w-lg p-8">
      <Alert variant={is404 ? 'default' : 'destructive'} role="alert">
        <AlertTriangle />
        <AlertTitle>{is404 ? 'Page not found' : 'Something went wrong'}</AlertTitle>
        <AlertDescription>
          {!is404 && <p>{getErrorMessage(error)}</p>}
          <Button asChild variant="outline" size="sm" className="mt-2">
            <Link to="/">Back to home</Link>
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
